import type { PixelEvent } from '@/events';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type {
  HeartbeatActivityState,
  HeartbeatConfig,
  HeartbeatMetrics,
  HeartbeatSnapshot,
} from '../contracts/heartbeat';
import type { SchedulerHandle } from '../contracts/scheduler';
import type { BehaviorSignalBus } from '../contracts/signals';
import { BehaviorSignalBus as SignalBusImpl } from '../runtime/BehaviorSignalBus';
import { ManagedScheduler } from '../runtime/ManagedScheduler';

import { HeartbeatPolicy } from './HeartbeatPolicy';
import { HeartbeatSnapshotBuilder } from './HeartbeatSnapshotBuilder';

export type HeartbeatEngineOptions = {
  readonly config: HeartbeatConfig;
  readonly signalBus?: BehaviorSignalBus;
};

/**
 * Heartbeat mede presença e publica BEHAVIOR_HEARTBEAT via Dispatcher.
 * Não chama Transport, Queue, Sender, Retry ou API.
 *
 * Sequence: reinicia em 0 a cada init (ciclo de vida fresco após destroy/reinit).
 */
export class HeartbeatEngine {
  readonly id = 'heartbeat';

  private readonly context: PixelRuntimeContext;

  private readonly config: HeartbeatConfig;

  private readonly signalBus: BehaviorSignalBus;

  private readonly ownsBus: boolean;

  private readonly scheduler = new ManagedScheduler();

  private readonly policy: HeartbeatPolicy;

  private readonly snapshotBuilder: HeartbeatSnapshotBuilder;

  private initialized = false;

  private running = false;

  private destroyed = false;

  private tickHandle: SchedulerHandle | null = null;

  private unsubscribers: Array<() => void> = [];

  private sequence = 0;

  private emittedCount = 0;

  private skippedCount = 0;

  private lastEmittedAt: number | undefined;

  private nextExpectedAt: number | undefined;

  private startedAt = 0;

  private activity: MutableActivity = {
    visible: undefined,
    idle: undefined,
    lastActivityAt: undefined,
    idleSince: undefined,
    visibleSince: undefined,
    hiddenSince: undefined,
  };

  constructor(context: PixelRuntimeContext, options: HeartbeatEngineOptions) {
    this.context = context;
    this.config = options.config;
    if (options.signalBus) {
      this.signalBus = options.signalBus;
      this.ownsBus = false;
    } else {
      this.signalBus = new SignalBusImpl();
      this.ownsBus = true;
    }
    this.policy = new HeartbeatPolicy(options.config);
    this.snapshotBuilder = new HeartbeatSnapshotBuilder(options.config);
  }

  init(): void {
    if (this.initialized && !this.destroyed) {
      return;
    }

    this.destroyed = false;
    this.scheduler.reset();
    this.sequence = 0;
    this.emittedCount = 0;
    this.skippedCount = 0;
    this.lastEmittedAt = undefined;
    this.nextExpectedAt = undefined;
    this.tickHandle = null;
    this.startedAt = Date.now();
    this.activity = {
      visible: undefined,
      idle: undefined,
      lastActivityAt: undefined,
      idleSince: undefined,
      visibleSince: undefined,
      hiddenSince: undefined,
    };

    this.seedEnvironmentState();
    this.bindSignals();

    this.initialized = true;
    this.running = true;

    if (this.config.fireImmediately) {
      this.tick();
    } else {
      this.scheduleNext(
        this.policy.resolveIntervalMs(this.readActivityState())
      );
    }
  }

  destroy(): void {
    if (this.destroyed && !this.initialized) {
      return;
    }

    this.running = false;
    this.destroyed = true;
    this.cancelTick();
    this.scheduler.destroy();
    this.unbindSignals();
    this.nextExpectedAt = undefined;

    if (this.ownsBus && this.signalBus instanceof SignalBusImpl) {
      this.signalBus.clear();
    }

    this.initialized = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  isRunning(): boolean {
    return this.running;
  }

  getMetrics(): HeartbeatMetrics {
    return {
      sequence: this.sequence,
      emittedCount: this.emittedCount,
      skippedCount: this.skippedCount,
      lastEmittedAt: this.lastEmittedAt,
      nextExpectedAt: this.nextExpectedAt,
      lastDriftMs: this.scheduler.lastDriftMs(),
    };
  }

  getSnapshot(): HeartbeatSnapshot {
    return {
      enabled: this.config.enabled,
      initialized: this.initialized,
      running: this.running,
      intervalMs: this.config.intervalMs,
      hiddenIntervalMs: this.config.hiddenIntervalMs,
      sequence: this.sequence,
      emittedCount: this.emittedCount,
      skippedCount: this.skippedCount,
      lastEmittedAt: this.lastEmittedAt,
      nextExpectedAt: this.nextExpectedAt,
      lastDriftMs: this.scheduler.lastDriftMs(),
    };
  }

  /** Contagem de timers ativos no scheduler (testes / diagnostics). */
  activeTimerCount(): number {
    return this.scheduler.activeCount();
  }

  private seedEnvironmentState(): void {
    if (typeof document === 'undefined') return;

    const now = Date.now();
    const isVisible = document.visibilityState === 'visible';
    this.activity.visible = isVisible;
    if (isVisible) {
      this.activity.visibleSince = now;
      this.activity.hiddenSince = undefined;
    } else {
      this.activity.hiddenSince = now;
      this.activity.visibleSince = undefined;
    }
  }

  private bindSignals(): void {
    this.unbindSignals();

    this.unsubscribers.push(
      this.signalBus.subscribe('ACTIVITY', signal => {
        this.activity.lastActivityAt = signal.timestamp;
        this.activity.idle = false;
        this.activity.idleSince = undefined;
      })
    );

    this.unsubscribers.push(
      this.signalBus.subscribe('IDLE', signal => {
        this.activity.idle = true;
        this.activity.idleSince = signal.timestamp;
        const since = signal.data?.idleSince;
        if (typeof since === 'number') {
          this.activity.idleSince = since;
        }
      })
    );

    this.unsubscribers.push(
      this.signalBus.subscribe('VISIBLE', signal => {
        this.activity.visible = true;
        this.activity.visibleSince = signal.timestamp;
        this.activity.hiddenSince = undefined;
        this.rescheduleForVisibilityChange();
      })
    );

    this.unsubscribers.push(
      this.signalBus.subscribe('HIDDEN', signal => {
        this.activity.visible = false;
        this.activity.hiddenSince = signal.timestamp;
        this.rescheduleForVisibilityChange();
      })
    );

    if (typeof document !== 'undefined') {
      const onVisibility = (): void => {
        if (typeof document === 'undefined') return;
        const now = Date.now();
        if (document.visibilityState === 'visible') {
          this.activity.visible = true;
          this.activity.visibleSince = now;
          this.activity.hiddenSince = undefined;
        } else {
          this.activity.visible = false;
          this.activity.hiddenSince = now;
        }
        this.rescheduleForVisibilityChange();
      };
      document.addEventListener('visibilitychange', onVisibility);
      this.unsubscribers.push(() => {
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibility);
        }
      });
    }
  }

  /** Transição de visibilidade: reagenda com intervalMs ou hiddenIntervalMs. */
  private rescheduleForVisibilityChange(): void {
    if (!this.running || this.destroyed) return;
    this.scheduleNext(this.policy.resolveIntervalMs(this.readActivityState()));
  }

  private unbindSignals(): void {
    for (const unsub of this.unsubscribers) {
      try {
        unsub();
      } catch {
        // ignore
      }
    }
    this.unsubscribers = [];
  }

  private scheduleNext(delayMs: number): void {
    this.cancelTick();
    if (this.destroyed || !this.running) return;

    this.nextExpectedAt = Date.now() + delayMs;
    this.tickHandle = this.scheduler.schedule(() => this.tick(), delayMs);
  }

  private cancelTick(): void {
    if (this.tickHandle) {
      this.scheduler.cancel(this.tickHandle);
      this.tickHandle = null;
    }
  }

  private tick(): void {
    this.tickHandle = null;

    if (this.destroyed || !this.running) {
      return;
    }

    const state = this.readActivityState();
    const decision = this.policy.canEmit(state, {
      running: this.running,
      destroyed: this.destroyed,
    });

    if (!decision.allow) {
      this.skippedCount += 1;
    } else {
      this.emitHeartbeat(state);
    }

    this.scheduleNext(this.policy.resolveIntervalMs(state));
  }

  private readActivityState(): HeartbeatActivityState {
    return {
      startedAt: this.startedAt,
      visible: this.activity.visible,
      idle: this.activity.idle,
      lastActivityAt: this.activity.lastActivityAt,
      idleSince: this.activity.idleSince,
      visibleSince: this.activity.visibleSince,
      hiddenSince: this.activity.hiddenSince,
    };
  }

  private emitHeartbeat(state: HeartbeatActivityState): void {
    this.sequence += 1;
    const now = Date.now();
    const data = this.snapshotBuilder.build(state, this.sequence, now);

    try {
      const event = this.context.services.eventBuilder.build({
        type: 'BEHAVIOR_HEARTBEAT',
        category: 'ENGAGEMENT',
        ...data,
      }) as PixelEvent;

      this.context.services.dispatcher.dispatch(event);
      this.emittedCount += 1;
      this.lastEmittedAt = now;

      // Sinal interno para consumidores (ex.: Engagement Runtime).
      this.signalBus.publish(
        this.signalBus.createSignal('HEARTBEAT', 'HeartbeatEngine', {
          sequence: this.sequence,
        })
      );
    } catch {
      // Isolamento: falha de listener/builder não derruba o engine.
      this.skippedCount += 1;
      this.context.logger.warn(
        '[Pixel] Heartbeat emit failed (listener isolated)'
      );
    }
  }
}

type MutableActivity = {
  visible: boolean | undefined;
  idle: boolean | undefined;
  lastActivityAt: number | undefined;
  idleSince: number | undefined;
  visibleSince: number | undefined;
  hiddenSince: number | undefined;
};
